import {
  Alert,
  Anchor,
  Button,
  Flex,
  List,
  Loader,
  Modal,
  PasswordInput,
  TextInput,
  Select,
  Stack,
  Text,
  Tabs,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useLiveQuery } from "dexie-react-hooks";
import { cloneElement, ReactElement, useEffect, useMemo, useState } from "react";
import { db, Settings } from "../db";
import { 
  providerRegistry, 
  validateCredentials, 
  ProviderModel,
  fetchModelsAndUpdateRegistry 
} from "../utils/providers";
import { ThemeSelector } from "./dynamic";

export function SettingsModal({ children }: { children: ReactElement }) {
  const [opened, { open, close }] = useDisclosure(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("provider");
  const [updatingModels, setUpdatingModels] = useState(false);

  // Provider settings
  const [provider, setProvider] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState<string | null>(null);
  const [apiAuth, setApiAuth] = useState<string | null>(null);
  const [apiBase, setApiBase] = useState("");
  const [apiVersion, setApiVersion] = useState("");

  // Get current settings from database
  const settings = useLiveQuery(async () => {
    return db.settings.where({ id: "general" }).first();
  });

  // Get all available providers
  const providers = useMemo(() => {
    return providerRegistry.getProvidersAsOptions();
  }, []);

  // Initialize settings from database
  useEffect(() => {
    if (settings) {
      // Set provider
      setProvider(settings.provider || null);
      
      // Set model
      setModel(settings.model || null);
      
      // Get provider-specific settings
      if (settings.provider && settings.providerSettings && settings.providerSettings[settings.provider]) {
        const providerSettings = settings.providerSettings[settings.provider];
        setApiKey(providerSettings.apiKey || "");
        setApiAuth(providerSettings.apiAuth || "bearer-token");
        setApiBase(providerSettings.apiBase || "");
        setApiVersion(providerSettings.apiVersion || "");
      } else {
        // Default values if no provider settings exist
        setApiKey("");
        setApiAuth("bearer-token");
        setApiBase("");
        setApiVersion("");
      }
    }
  }, [settings]);

  // Get available models for the selected provider
  const availableModels = useMemo(() => {
    if (!provider) return [];
    // Ensure we get the latest models from the registry if they were updated
    const currentProviderData = providerRegistry.get(provider);
    if (!currentProviderData) return [];
    return currentProviderData.models.map((m) => ({
        value: m.id,
        label: m.name,
    }));
  }, [provider]); // Re-run when provider changes

  // Get the selected provider
  const selectedProvider = useMemo(() => {
    if (!provider) return null;
    return providerRegistry.get(provider);
  }, [provider]);

  // Function to handle manual model update
  const handleUpdateModels = async () => {
    if (!provider || provider !== 'anthropic') {
      notifications.show({
        title: "Cannot Update",
        message: "Select the Anthropic provider to update models.",
        color: "orange",
      });
      return;
    }
    
    // Get provider settings to check for API key
    if (!settings || !settings.providerSettings || !settings.providerSettings['anthropic'] || !settings.providerSettings['anthropic'].apiKey) {
      notifications.show({
        title: "Cannot Update",
        message: "Please set an API key for Anthropic first.",
        color: "orange",
      });
      return;
    }
    
    const anthropicApiKey = settings.providerSettings['anthropic'].apiKey;

    setUpdatingModels(true);
    notifications.show({
      id: 'update-models-start',
      title: "Updating Models",
      message: `Fetching latest models from Anthropic...`,
      loading: true,
      autoClose: false,
    });

    try {
      // --- SECURITY WARNING ---
      // Making this API call directly from the frontend exposes the API key in the browser's network requests.
      // A backend proxy is the recommended secure approach for production applications.
      const response = await fetch('https://api.anthropic.com/v1/models', {
        method: 'GET',
        headers: {
          'x-api-key': anthropicApiKey,
          'anthropic-version': '2023-06-01' // Or your preferred version
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Anthropic API error (${response.status}): ${errorText}`);
      }

      const result = await response.json();

      const fetchedApiModels = result.data || result; // Adjust based on actual structure

      if (!Array.isArray(fetchedApiModels)) {
         console.error("Unexpected response structure:", result);
        throw new Error("Received unexpected data structure from Anthropic API.");
      }

      // Map the fetched models to the ProviderModel structure
      const newModels: ProviderModel[] = fetchedApiModels.map((model: any) => ({
        id: model.id, // Assuming 'id' field exists
        name: model.name || model.id, // Assuming 'name' or 'id' field exists for the label
        // Add other fields like description, contextWindow if available in the response and needed
      })).sort((a, b) => a.name.localeCompare(b.name)); // Sort models alphabetically

      // Get the provider object from the registry
      const anthropicProvider = providerRegistry.get('anthropic');
      if (anthropicProvider) {
        // Update the models array IN MEMORY for this session
        anthropicProvider.models = newModels;
        console.log("Updated Anthropic models in memory:", anthropicProvider.models);

         // Force re-render of model selector by briefly changing provider state
         // This ensures the useMemo for availableModels recalculates
         const currentModel = model;
         setModel(null); // Clear model momentarily
         // Trigger the memo recalculation by setting the provider state again (even to the same value)
         // Use a minimal delay to ensure state updates are processed
         setTimeout(() => {
             setProvider(prev => prev); // Setting to previous value is enough to trigger useEffect/useMemo
             // Try to restore the previously selected model if it still exists, otherwise select the first
             const restoredModelExists = newModels.some(m => m.id === currentModel);
             const defaultModel = newModels.length > 0 ? newModels[0].id : null;
             const modelToSet = restoredModelExists ? currentModel : defaultModel;
             setModel(modelToSet);
             // Also update the model in the database if it changed
             if (modelToSet !== settings?.model) {
                saveSetting("model", modelToSet, "Model updated after fetching list.");
             }
         }, 10); // Small delay

        notifications.update({
           id: 'update-models-start',
           title: "Update Successful",
           message: `Fetched ${newModels.length} models from Anthropic.`,
           color: 'green',
           loading: false,
           autoClose: 5000,
        });

      } else {
         throw new Error("Could not find Anthropic provider in registry.");
      }

    } catch (error: any) {
       console.error("Failed to update Anthropic models:", error);
       notifications.update({
          id: 'update-models-start',
          title: "Update Failed",
          message: error.message || "Could not fetch models from Anthropic.",
          color: "red",
          loading: false,
          autoClose: 7000,
       });
    } finally {
      setUpdatingModels(false);
    }
  };

  // Function to fetch models from the selected provider
  const fetchModelsFromProvider = async () => {
    if (!provider || updatingModels) return;
    
    setUpdatingModels(true);
    notifications.show({
      id: 'fetching-models',
      title: "Fetching Models",
      message: `Fetching models for ${provider}...`,
      loading: true,
      autoClose: false,
    });
    
    try {
      console.log(`Starting model fetch for provider: ${provider}`);
      // Use the new helper function to fetch models and update the registry
      const models = await fetchModelsAndUpdateRegistry(provider);
      
      notifications.update({
        id: 'fetching-models',
        title: "Models Fetched",
        message: `Successfully fetched ${models.length} models for ${provider}.`,
        color: "green",
        loading: false,
        autoClose: 3000,
      });
      
      // Force a refresh of the available models in the UI
      setTimeout(() => {
        // This is needed to force a refresh of the provider state which triggers model refresh
        setProvider(prev => {
          console.log(`Refreshing provider state from: ${prev}`);
          return prev;
        });
      }, 300);
    } catch (error: any) {
      console.error("Error fetching models:", error);
      notifications.update({
        id: 'fetching-models',
        title: "Failed to Fetch Models",
        message: error.message || "An error occurred while fetching models.",
        color: "red",
        loading: false,
        autoClose: 5000,
      });
    } finally {
      setUpdatingModels(false);
    }
  };

  // Save a setting to the database
  const saveSetting = async (key: string, value: any, successMessage: string) => {
    // Prevent saving during model update to avoid conflicts
    if (updatingModels && (key === 'provider' || key === 'model')) return;
    
    setSubmitting(true);
    try {
      if (key === 'provider' || key === 'model') {
        // These are top-level settings
        const updateData: Partial<Settings> = {
          [key]: value === null ? undefined : value
        };
        await db.settings.update("general", updateData);
      } else {
        // These are provider-specific settings
        if (!provider) {
          throw new Error("No provider selected");
        }
        
        // Get current settings
        const currentSettings = await db.settings.get("general");
        if (!currentSettings) {
          throw new Error("Settings not found");
        }
        
        // Initialize providerSettings if needed
        if (!currentSettings.providerSettings) {
          currentSettings.providerSettings = {};
        }
        
        // Initialize provider settings if needed
        if (!currentSettings.providerSettings[provider]) {
          currentSettings.providerSettings[provider] = {};
        }
        
        // Check if this is an apiKey being set for the first time
        const isNewApiKey = key === 'apiKey' && 
                           (!currentSettings.providerSettings[provider].apiKey || 
                            currentSettings.providerSettings[provider].apiKey !== value);
        
        // Update the specific setting
        currentSettings.providerSettings[provider][key] = value === null ? undefined : value;
        
        // Save back to DB
        await db.settings.update("general", {
          providerSettings: currentSettings.providerSettings
        });
        
        // If this is a new API key and it's not empty, try to fetch models
        if (isNewApiKey && value) {
          // Set a small delay to ensure the API key is saved before fetching models
          setTimeout(() => fetchModelsFromProvider(), 500);
        }
      }

      notifications.show({
        title: "Saved",
        message: successMessage,
      });
    } catch (error: any) {
      console.error("Save setting error:", error);
      const message = error.message || "Failed to save setting.";
      notifications.show({
        title: "Error",
        color: "red",
        message: message,
      });
    } finally {
      // Only set submitting false if not updating models (which has its own flag)
      if (!updatingModels) {
         setSubmitting(false);
      }
    }
  };

  return (
    <>
      {cloneElement(children, { onClick: open })}
      <Modal opened={opened} onClose={close} title="Settings" size="lg">
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="provider">Provider</Tabs.Tab>
            <Tabs.Tab value="appearance">Appearance</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="provider" pt="xs">
            <Stack>
              <Select
                label="Provider"
                value={provider}
                onChange={async (value) => {
                  if (value) {
                    const previousProvider = provider;
                    setProvider(value); // Set provider state first
                    await saveSetting('provider', value, "Provider setting saved.");
                    
                    // Only reset model if the provider actually changed
                    if (value !== previousProvider) {
                        // Get the first model for the new provider from registry (static list initially)
                        const providerData = providerRegistry.get(value);
                        // Safely access properties
                        const defaultModel = providerData?.defaultModel;
                        const firstModel = (providerData?.models && providerData.models.length > 0) 
                                           ? providerData.models[0].id 
                                           : null;
                        const modelToSet = defaultModel || firstModel;

                        setModel(modelToSet); // Set model state
                        if (modelToSet !== settings?.model) {
                            await saveSetting('model', modelToSet, "Model reset for new provider.");
                        }
                    }
                  }
                }}
                data={providers}
                withinPortal
                disabled={submitting || updatingModels} // Disable during model update too
                searchable
              />

              {/* Conditionally render API Key / Custom Settings based on provider */}
              {selectedProvider?.authType === 'bearer-token' && provider !== 'custom' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                  <PasswordInput
                    label="API Key"
                    placeholder="Enter your API key"
                    value={apiKey}
                    onChange={(event) => setApiKey(event.currentTarget.value)}
                    disabled={submitting || updatingModels}
                    style={{ flexGrow: 1 }}
                  />
                  <Button 
                    onClick={() => {
                      saveSetting('apiKey', apiKey, "API key saved and models fetched.");
                      // Close the modal after a short delay to let models fetch
                      setTimeout(() => close(), 1000);
                    }}
                    disabled={submitting || updatingModels || !apiKey}
                    loading={submitting || updatingModels}
                  >
                    {updatingModels ? "Fetching..." : "Save"}
                  </Button>
                </div>
              )}

              {selectedProvider?.authType === 'api-key' && provider !== 'custom' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                  <PasswordInput
                    label="API Key"
                    placeholder="Enter your API key"
                    value={apiKey}
                    onChange={(event) => setApiKey(event.currentTarget.value)}
                    disabled={submitting || updatingModels}
                    style={{ flexGrow: 1 }}
                  />
                  <Button 
                    onClick={() => {
                      saveSetting('apiKey', apiKey, "API key saved and models fetched.");
                      // Close the modal after a short delay to let models fetch
                      setTimeout(() => close(), 1000);
                    }}
                    disabled={submitting || updatingModels || !apiKey}
                    loading={submitting || updatingModels}
                  >
                    {updatingModels ? "Fetching..." : "Save"}
                  </Button>
                </div>
              )}

              {provider === 'custom' && (
                <>
                   <TextInput
                      label="API Base URL"
                      placeholder="e.g. https://your-azure.openai.azure.com/"
                      value={apiBase}
                      onChange={(event) => setApiBase(event.currentTarget.value)}
                      onBlur={() => saveSetting('apiBase', apiBase, "API Base URL saved.")}
                      disabled={submitting || updatingModels}
                    />
                   <Select
                      label="API Authentication"
                      value={apiAuth}
                      onChange={(value) => {
                        setApiAuth(value);
                        saveSetting('apiAuth', value, "API Authentication type saved.");
                      }}
                      data={[
                        { value: 'bearer-token', label: 'Bearer Token (API Key in header)' },
                        { value: 'api-key', label: 'API Key (api-key in header)' },
                        // Add other auth types if needed
                      ]}
                      disabled={submitting || updatingModels}
                      withinPortal
                    />
                    {(apiAuth === 'bearer-token' || apiAuth === 'api-key') && (
                        <PasswordInput
                          label="API Key"
                          placeholder="Enter your API key"
                          value={apiKey}
                          onChange={(event) => setApiKey(event.currentTarget.value)}
                          onBlur={() => saveSetting('apiKey', apiKey, "API key saved.")}
                          disabled={submitting || updatingModels}
                        />
                    )}
                   <TextInput
                      label="API Version (Optional)"
                      placeholder="e.g. 2023-05-15"
                      value={apiVersion}
                      onChange={(event) => setApiVersion(event.currentTarget.value)}
                      onBlur={() => saveSetting('apiVersion', apiVersion, "API Version saved.")}
                      disabled={submitting || updatingModels}
                    />
                </>
              )}

              {/* API Key Help Text */}
              <List withPadding>
                {selectedProvider?.apiKeyUrl && (
                  <List.Item>
                    <Text size="sm">
                      <Anchor
                        href={selectedProvider.apiKeyUrl}
                        target="_blank"
                      >
                        Get your API key for {selectedProvider.name}
                      </Anchor>
                    </Text>
                  </List.Item>
                )}
                <List.Item>
                  <Text size="sm" color="dimmed">
                    The API Key is stored locally on your browser only.
                  </Text>
                </List.Item>
              </List>

              {/* Model selection has been removed from settings modal
                  and will only be available in the chat interface */}

            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="appearance" pt="xs">
            <Stack>
              <ThemeSelector />
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Modal>
    </>
  );
}
